import { create } from 'zustand';
import type { ExecutionDoc, TaskDoc, UserId } from '@mct/shared';
import { getDb } from './firebase';
import {
  restoreTask,
  deleteTask,
  updateExecution,
  updateTask,
} from './firestore';

/**
 * App-level Undo (Cmd+Z / Ctrl+Z).
 *
 * A single in-memory stack of reversible edits. Each entry knows how to
 * `undo()` (restore the prior state) and `redo()` (re-apply the change)
 * by issuing a fresh Firestore write — undo is *not* a local rollback, it
 * is another committed update, so the result syncs to every client like
 * any other edit.
 *
 * Scope: session-only, per browser tab, reset on project switch. History
 * is capped at {@link MAX_HISTORY}; the requirement floor is 10, we keep 50.
 */

/** History depth. Spec floor is 10; we keep the stretch target of 50. */
export const MAX_HISTORY = 50;

export interface UndoableEdit {
  /** Stable tag for tests / debugging (e.g. `'task-update'`). */
  type: string;
  /**
   * Descriptive fragment for the toast — the store appends
   * 「…を元に戻しました」/「…をやり直しました」. e.g. `'"設計レビュー" の依存'`.
   */
  label: string;
  /** Revert to the state before the edit. */
  undo: () => Promise<void>;
  /** Re-apply the edit (Redo). */
  redo: () => Promise<void>;
}

export interface UndoToastState {
  message: string;
  kind: 'undo' | 'redo' | 'error';
}

interface UndoStoreState {
  /** Edits available to undo, oldest first; the last element is "next". */
  past: UndoableEdit[];
  /** Edits available to redo, produced by undo(). */
  future: UndoableEdit[];
  /** Transient toast surfaced by {@link UndoToast}. */
  toast: UndoToastState | null;
  record: (edit: UndoableEdit) => void;
  undo: () => Promise<boolean>;
  redo: () => Promise<boolean>;
  dismissToast: () => void;
  reset: () => void;
}

export const useUndoStore = create<UndoStoreState>((set, get) => ({
  past: [],
  future: [],
  toast: null,

  record: (edit) =>
    set((s) => ({
      // Cap from the *front* so the newest entry always survives.
      past: [...s.past, edit].slice(-MAX_HISTORY),
      // Any new edit invalidates the redo branch.
      future: [],
    })),

  undo: async () => {
    const entry = get().past.at(-1);
    if (!entry) return false;
    // Pop optimistically so a second Cmd+Z can't re-enter the same entry
    // while its write is in flight.
    set((s) => ({ past: s.past.slice(0, -1) }));
    try {
      await entry.undo();
    } catch (err) {
      console.error('[undo] failed', err);
      set((s) => ({
        past: [...s.past, entry],
        toast: { message: '元に戻せませんでした', kind: 'error' },
      }));
      return false;
    }
    set((s) => ({
      future: [...s.future, entry].slice(-MAX_HISTORY),
      toast: { message: `${entry.label}を元に戻しました`, kind: 'undo' },
    }));
    return true;
  },

  redo: async () => {
    const entry = get().future.at(-1);
    if (!entry) return false;
    set((s) => ({ future: s.future.slice(0, -1) }));
    try {
      await entry.redo();
    } catch (err) {
      console.error('[redo] failed', err);
      set((s) => ({
        future: [...s.future, entry],
        toast: { message: 'やり直せませんでした', kind: 'error' },
      }));
      return false;
    }
    set((s) => ({
      past: [...s.past, entry].slice(-MAX_HISTORY),
      toast: { message: `${entry.label}をやり直しました`, kind: 'redo' },
    }));
    return true;
  },

  dismissToast: () => set({ toast: null }),
  reset: () => set({ past: [], future: [], toast: null }),
}));

/** Imperative push for non-React call sites (Firestore wrappers, handlers). */
export function recordEdit(edit: UndoableEdit): void {
  useUndoStore.getState().record(edit);
}

/**
 * Build the inverse patch for `updateDoc`-style edits: for every key the
 * forward patch touches, read the prior value off the `before` snapshot.
 * Pure — the caller owns when to push it.
 */
export function inversePatch<T extends object>(
  before: T,
  patch: Partial<T>,
): Partial<T> {
  const inverse: Partial<T> = {};
  for (const key of Object.keys(patch) as (keyof T)[]) {
    inverse[key] = before[key];
  }
  return inverse;
}

/**
 * Record a reversible TaskDoc field edit. `before` is the doc as it was
 * *before* the forward write the caller already issued; `patch` is the set
 * of changed fields. Undo writes the prior values, redo re-writes `patch`.
 */
export function recordTaskEdit(args: {
  projectId: string;
  taskId: string;
  uid: UserId;
  before: TaskDoc;
  patch: Partial<TaskDoc>;
  label: string;
}): void {
  const inverse = inversePatch(args.before, args.patch);
  recordEdit({
    type: 'task-update',
    label: args.label,
    undo: () =>
      updateTask(getDb(), args.projectId, args.taskId, args.uid, inverse),
    redo: () =>
      updateTask(getDb(), args.projectId, args.taskId, args.uid, args.patch),
  });
}

/** Record a reversible ExecutionDoc field edit (status / notes). */
export function recordExecutionEdit(args: {
  projectId: string;
  runId: string;
  taskId: string;
  uid: UserId;
  before: Pick<ExecutionDoc, 'status' | 'handoverNotes' | 'successorNotes'>;
  patch: Partial<
    Pick<ExecutionDoc, 'status' | 'handoverNotes' | 'successorNotes'>
  >;
  label: string;
}): void {
  const inverse = inversePatch(args.before, args.patch);
  recordEdit({
    type: 'execution-update',
    label: args.label,
    undo: () =>
      updateExecution(
        getDb(),
        args.projectId,
        args.runId,
        args.taskId,
        args.uid,
        inverse,
      ),
    redo: () =>
      updateExecution(
        getDb(),
        args.projectId,
        args.runId,
        args.taskId,
        args.uid,
        args.patch,
      ),
  });
}

/**
 * Record a reversible task deletion. `task` is the snapshot captured just
 * before `deleteTask` ran; undo re-creates the doc, redo deletes it again.
 */
export function recordTaskDelete(args: {
  task: TaskDoc;
  label: string;
}): void {
  const { task } = args;
  recordEdit({
    type: 'task-delete',
    label: args.label,
    undo: () => restoreTask(getDb(), task),
    redo: () => deleteTask(getDb(), task.projectId, task.taskId),
  });
}
